import { toCanvas } from "html-to-image";

// REUSABLE screenshot capture for the running app document. Used by the Team
// Notes widget and the builder's preview screenshot tool (postMessage bridge);
// future features should import this rather than rolling their own capture.
//
// Elements marked with the exclude attribute (platform overlays, the widget
// itself) are filtered out of the capture. Any failure (cross-origin image
// taint, CSP, huge DOM) resolves to NULL — callers must treat the screenshot
// as optional and never block on it.
export const CAPTURE_EXCLUDE_ATTR = "data-agentui-capture-exclude";

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.75;

export async function captureScreenshot({
    exclude = `[${CAPTURE_EXCLUDE_ATTR}]`,
} = {}) {
    try {
        // Capture only the VIEWPORT, not the full scrollable document. A tall
        // dashboard (e.g. 3000×15000) would otherwise allocate ~180MB and crash
        // mobile tabs; bounding to innerWidth×innerHeight fixes the OOM AND
        // keeps the capture aligned with viewport-relative pin coordinates. The
        // scroll transform brings the currently-visible area into frame.
        const vw = Math.min(window.innerWidth, MAX_DIMENSION * 2);
        const vh = Math.min(window.innerHeight, MAX_DIMENSION * 2);
        const canvas = await toCanvas(document.body, {
            cacheBust: true,
            width: vw,
            height: vh,
            canvasWidth: vw,
            canvasHeight: vh,
            style: {
                transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)`,
                transformOrigin: "top left",
            },
            filter: (node) => {
                if (!node || node.nodeType !== 1) return true;
                try {
                    return !node.matches(exclude);
                } catch {
                    return true;
                }
            },
        });

        const scale = Math.min(
            1,
            MAX_DIMENSION / Math.max(canvas.width, canvas.height)
        );
        if (scale >= 1) return canvas.toDataURL("image/jpeg", JPEG_QUALITY);

        const scaled = document.createElement("canvas");
        scaled.width = Math.round(canvas.width * scale);
        scaled.height = Math.round(canvas.height * scale);
        scaled
            .getContext("2d")
            .drawImage(canvas, 0, 0, scaled.width, scaled.height);
        return scaled.toDataURL("image/jpeg", JPEG_QUALITY);
    } catch (error) {
        console.warn("[screenshot] capture failed:", error?.message);
        return null;
    }
}
